/**
 * Products API client (Phase 3b Step 6).
 * Uses relative `/api` — Vite proxy injects X-Api-Token server-side.
 * Do NOT set X-Api-Token from the browser.
 */

import type { CatalogItem } from '@/services/catalog'
import {
  listColorsApi,
  listVeneersApi,
  listWoodTypesApi,
} from '@/services/catalog/apiClient'
import type {
  ChangeItemCodeInput,
  PagedResult,
  Product,
  ProductInput,
  ProductListParams,
  ProductStatus,
  ProductUpdateInput,
} from './types'

export type ProductsApiError = {
  ok: false
  error: { code: string; message: string; status: number }
}

export class ProductsApiClientError extends Error {
  readonly code: string
  readonly status: number

  constructor(code: string, message: string, status: number) {
    super(message)
    this.name = 'ProductsApiClientError'
    this.code = code
    this.status = status
  }
}

export type ProductCustomerApiDto = {
  id: string
  code: string
  name: string
  status: string
}

export type ProductApiDto = {
  id: string
  item_code: string
  name: string
  status: ProductStatus
  customer_id?: string | null
  customer?: ProductCustomerApiDto | null
  w: number | null
  d: number | null
  h: number | null
  note: string | null
  image_url: string | null
  colorIds: string[]
  woodTypeIds: string[]
  veneerIds: string[]
  created_by: string | null
  updated_by: string | null
  version: number
  created_at: string
  updated_at: string
}

async function parseJson<T>(res: Response): Promise<T> {
  return (await res.json()) as T
}

function throwIfApiError(res: Response, body: unknown, fallback: string): void {
  if (res.ok) return
  const err = body as ProductsApiError
  throw new ProductsApiClientError(
    err.error?.code ?? 'HTTP_ERROR',
    err.error?.message ?? `${fallback} (${res.status})`,
    err.error?.status ?? res.status,
  )
}

function requireDimension(
  value: number | null,
  field: 'w' | 'd' | 'h',
  productId: string,
): number {
  if (value == null || !Number.isFinite(value)) {
    throw new ProductsApiClientError(
      'INVALID_PRODUCT_DTO',
      `Product ${productId}: field "${field}" is null/invalid from API — UI requires number (no silent default).`,
      500,
    )
  }
  return value
}

function resolveAttr(
  items: CatalogItem[],
  id: string,
  kind: string,
): CatalogItem {
  const found = items.find((item) => item.id === id)
  if (!found) {
    throw new ProductsApiClientError(
      'CATALOG_REF_MISSING',
      `Product references ${kind} id "${id}" but it was not returned by catalog API (missing or inactive).`,
      422,
    )
  }
  return found
}

/**
 * Map API DTO → UI Product.
 * Field renames: item_code→code, woodTypeIds→woodIds, image_url→imageUrl.
 * Hydrates colors/woods/veneers from catalog lists (IDs only on DTO).
 */
export function productApiDtoToUi(
  dto: ProductApiDto,
  catalog: {
    colors: CatalogItem[]
    woods: CatalogItem[]
    veneers: CatalogItem[]
  },
): Product {
  const colorIds = dto.colorIds ?? []
  const woodIds = dto.woodTypeIds ?? []
  const veneerIds = dto.veneerIds ?? []
  return {
    id: dto.id,
    code: dto.item_code,
    name: dto.name,
    customerId: dto.customer_id ?? null,
    customer: dto.customer ?? null,
    w: requireDimension(dto.w, 'w', dto.id),
    d: requireDimension(dto.d, 'd', dto.id),
    h: requireDimension(dto.h, 'h', dto.id),
    status: dto.status,
    // null note → explicit empty string (UI type is string, not inventing content)
    note: dto.note ?? '',
    imageUrl: dto.image_url ?? undefined,
    colorIds,
    woodIds,
    veneerIds,
    colors: colorIds.map((id) => resolveAttr(catalog.colors, id, 'color')),
    woods: woodIds.map((id) => resolveAttr(catalog.woods, id, 'wood')),
    veneers: veneerIds.map((id) => resolveAttr(catalog.veneers, id, 'veneer')),
  }
}

async function loadCatalogForHydrate(): Promise<{
  colors: CatalogItem[]
  woods: CatalogItem[]
  veneers: CatalogItem[]
}> {
  const [colors, woods, veneers] = await Promise.all([
    listColorsApi(true),
    listWoodTypesApi(true),
    listVeneersApi(true),
  ])
  return { colors, woods, veneers }
}

async function hydrateDto(dto: ProductApiDto): Promise<Product> {
  const catalog = await loadCatalogForHydrate()
  return productApiDtoToUi(dto, catalog)
}

export async function listProductsApi(
  params?: ProductListParams,
): Promise<PagedResult<Product>> {
  const query = new URLSearchParams()
  if (params?.customerId) query.set('customerId', params.customerId)
  if (params?.search) query.set('q', params.search)
  if (params?.lite) query.set('lite', '1')
  if (params?.status && params.status !== 'all') query.set('status', params.status)
  if (params?.colorIds?.length) query.set('colorIds', params.colorIds.join(','))
  if (params?.woodIds?.length) query.set('woodIds', params.woodIds.join(','))
  if (params?.veneerIds?.length) query.set('veneerIds', params.veneerIds.join(','))
  if (params?.sortBy) query.set('sortBy', params.sortBy)
  if (params?.sortDir) query.set('sortDir', params.sortDir)
  if (params?.page != null) query.set('page', String(params.page))
  if (params?.pageSize != null) query.set('pageSize', String(params.pageSize))

  const qs = query.toString() ? `?${query.toString()}` : ''
  const res = await fetch(`/api/products${qs}`)
  const body = await parseJson<
    | {
        ok: true
        products: ProductApiDto[]
        total?: number
        page?: number
        pageSize?: number
      }
    | ProductsApiError
  >(res)
  throwIfApiError(res, body, 'Failed to list products')
  const okBody = body as {
    products: ProductApiDto[]
    total?: number
    page?: number
    pageSize?: number
  }
  const catalog = await loadCatalogForHydrate()
  const items = okBody.products.map((dto) => productApiDtoToUi(dto, catalog))
  const total = okBody.total ?? items.length
  const page = okBody.page ?? params?.page ?? 1
  const pageSize = okBody.pageSize ?? params?.pageSize ?? items.length
  return { items, total, page, pageSize }
}

export async function getProductApi(id: string): Promise<Product | null> {
  const res = await fetch(`/api/products/${encodeURIComponent(id)}`)
  if (res.status === 404) return null
  const body = await parseJson<
    { ok: true; product: ProductApiDto } | ProductsApiError
  >(res)
  throwIfApiError(res, body, 'Failed to get product')
  return hydrateDto((body as { product: ProductApiDto }).product)
}

export async function getProductByCodeApi(code: string): Promise<Product | null> {
  const res = await fetch(
    `/api/products/by-code/${encodeURIComponent(code)}`,
  )
  if (res.status === 404) return null
  const body = await parseJson<
    { ok: true; product: ProductApiDto } | ProductsApiError
  >(res)
  throwIfApiError(res, body, 'Failed to get product by code')
  return hydrateDto((body as { product: ProductApiDto }).product)
}

function productInputToBody(input: ProductInput): Record<string, unknown> {
  return {
    item_code: input.code,
    name: input.name,
    customer_id: input.customerId ?? null,
    status: input.status,
    w: input.w,
    d: input.d,
    h: input.h,
    note: input.note,
    image_url: input.imageUrl ?? null,
    colorIds: input.colorIds,
    woodTypeIds: input.woodIds,
    veneerIds: input.veneerIds,
  }
}

function productUpdateToBody(input: ProductUpdateInput): Record<string, unknown> {
  const body: Record<string, unknown> = {}
  if (input.name !== undefined) body.name = input.name
  if (input.customerId !== undefined) body.customer_id = input.customerId
  if (input.status !== undefined) body.status = input.status
  if (input.w !== undefined) body.w = input.w
  if (input.d !== undefined) body.d = input.d
  if (input.h !== undefined) body.h = input.h
  if (input.note !== undefined) body.note = input.note
  if (input.imageUrl !== undefined) body.image_url = input.imageUrl ?? null
  if (input.colorIds !== undefined) body.colorIds = input.colorIds
  if (input.woodIds !== undefined) body.woodTypeIds = input.woodIds
  if (input.veneerIds !== undefined) body.veneerIds = input.veneerIds
  return body
}

export async function createProductApi(input: ProductInput): Promise<Product> {
  const res = await fetch('/api/products', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(productInputToBody(input)),
  })
  const body = await parseJson<
    { ok: true; product: ProductApiDto } | ProductsApiError
  >(res)
  throwIfApiError(res, body, 'Tạo Product thất bại')
  return hydrateDto((body as { product: ProductApiDto }).product)
}

export async function updateProductApi(
  id: string,
  input: ProductUpdateInput,
): Promise<Product> {
  const res = await fetch(`/api/products/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(productUpdateToBody(input)),
  })
  const body = await parseJson<
    { ok: true; product: ProductApiDto } | ProductsApiError
  >(res)
  throwIfApiError(res, body, 'Cập nhật Product thất bại')
  return hydrateDto((body as { product: ProductApiDto }).product)
}

export async function archiveProductApi(
  id: string,
  status: ProductStatus = 'inactive',
): Promise<Product> {
  const res = await fetch(`/api/products/${encodeURIComponent(id)}/archive`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status }),
  })
  const body = await parseJson<
    { ok: true; product: ProductApiDto } | ProductsApiError
  >(res)
  throwIfApiError(res, body, 'Archive Product thất bại')
  return hydrateDto((body as { product: ProductApiDto }).product)
}

export async function changeProductCodeApi(
  id: string,
  input: ChangeItemCodeInput,
): Promise<Product> {
  const payload: Record<string, unknown> = {
    new_code: input.newCode,
    reason: input.reason,
  }
  if (input.changedBy !== undefined) {
    payload.changed_by = input.changedBy
  }
  const res = await fetch(
    `/api/products/${encodeURIComponent(id)}/change-code`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
  )
  const body = await parseJson<
    { ok: true; product: ProductApiDto; history?: unknown } | ProductsApiError
  >(res)
  throwIfApiError(res, body, 'Đổi mã Product thất bại')
  return hydrateDto((body as { product: ProductApiDto }).product)
}
